import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import { Observable, of } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Brand } from '../interfaces/brand';
import { Category } from '../interfaces/category';
import { OnlineProduct } from '../interfaces/product';

/// One tag of the blog's cloud (Category/GetTagCloud): a category the shop lists products in, how many, and a size 1 to 5.
export interface TagCloudItem {
    name: string;
    slug: string;
    count: number;
    weight: number;
}

/// The server render's cloud, carried in the page so the browser does not ask for it again.
const TAG_CLOUD = makeStateKey<TagCloudItem[]>('tag-cloud');

@Injectable({
  providedIn: 'root'
})
export class CategoryService 
{ 
    /// The cloud, asked for once per visit and shared by every page that shows it.
    private cloud$: Observable<TagCloudItem[]> = null;

  	constructor( private http: HttpClient, private state: TransferState, @Inject(PLATFORM_ID) private platformId: any) { }

	getAllParentCategory(): Observable<any> 
	{ 
        return this.http.get<any>(environment.apiUrl + `/api/Category/GetAllParentCategory`); 
    }

	getCategoriesByParentId(parentId:number): Observable<any> 
	{ 
        return this.http.get<any>(environment.apiUrl + `/api/Category/GetSubCategoryByParentId/${parentId}`); 
    } 

    getCategoryMenu(): Observable<any> 
	{ 
        return this.http.get<any>(environment.apiUrl + `/api/Category/GetCategoryMenuV2`); 
    }

    getCategory(slug: string): Observable<Category> 
    { 
        return this.http.get<any>(environment.apiUrl + `/api/Category/GetCategoryBySlug/${slug}`);
    }

    getBrandBySlug(slug:string): Observable<Brand> 
	{ 
        return this.http.get<any>(environment.apiUrl + `/api/Category/GetBrandBySlug/${slug}`);
    }

    /// What a brand page with nothing on sale shows instead: products from the categories the
    /// brand's products were in, then the newest.
    /// Each card's live line: [{ Slug, FromPrice, NewestUtc, NewThisWeek, NewThisMonth }].
    getCategoryHighlights(slugs: string[]): Observable<any[]>
    {
        return this.http.get<any[]>(environment.apiUrl + '/api/Category/GetCategoryHighlights?slugs=' + encodeURIComponent(slugs.join(',')));
    }

    /// The blog sidebar's tag cloud: the categories that hold the most published products, alphabetical, each
    /// with its size. The same for every visitor and country, so - like the category cards' live line - it is
    /// asked for on the server, drawn in the server render and carried in the page: the browser shows the same
    /// tags at once and asks for nothing. A failed request is no tags and is not kept.
    getTagCloud(take: number = 30): Observable<TagCloudItem[]>
    {
        if (!this.cloud$)
        {
            const carried = this.state.get<TagCloudItem[]>(TAG_CLOUD, null);
            this.cloud$ = (carried ? of(carried) : this.http.get<TagCloudItem[]>(environment.apiUrl + '/api/Category/GetTagCloud?take=' + take).pipe(
                tap(tags => {
                    if (isPlatformServer(this.platformId)) {
                        this.state.set(TAG_CLOUD, tags || []);
                    }
                }),
                catchError(() => {
                    this.cloud$ = null;
                    return of([] as TagCloudItem[]);
                }))).pipe(shareReplay(1));
        }

        return this.cloud$;
    }

    getBrandAlternatives(slug:string): Observable<OnlineProduct[]>
    {
        return this.http.get<OnlineProduct[]>(environment.apiUrl + `/api/Category/GetBrandAlternatives/${encodeURIComponent(slug)}`);
    }


    /// A product description cleaned before the owner works on it: the mechanical cleanup always,
    /// the AI's language pass when it answers well (DescriptionCleaner on the server). Saves nothing.
    cleanDescription(name: string, html: string): Observable<{ html: string, usedAi: boolean, note: string }>
    {
        return this.http.post<{ html: string, usedAi: boolean, note: string }>(environment.apiUrl + '/api/AI/CleanDescription', { Name: name, Html: html });
    }

    /// Every category, flat ({id, parentId, name, slug}), for the product form's picker. Signed in only.
    getCategoryPicker(): Observable<{ id: number, parentId: number, name: string, slug: string }[]>
    {
        return this.http.get<{ id: number, parentId: number, name: string, slug: string }[]>(environment.apiUrl + '/api/Category/GetCategoryPicker');
    }

    //#region BRAND CALLS
    getAllBrands(): Observable<any> 
	{ 
        return this.http.get<any>(environment.apiUrl + `/api/Category/getAllBrands`); 
    }

    getBrandsByCategory(catSlug:string): Observable<any> 
	{ 
        return this.http.get<any>(environment.apiUrl + `/api/Category/GetBrandsByCategory/${catSlug}`); 
    }
    //#endregion

    //#region BRAND CALLS
    getAllProductFeatureTemplates(): Observable<any> 
	{ 
        return this.http.get<any>(environment.apiUrl + `/api/Category/GetFetureTemplates`); 
    }

    getAIGeneratedText(text:string, maxLength:number = 200): Observable<string> 
	{ 
        return this.http.get<any>(environment.apiUrl + `/api/AI/GetAIGeneratedText?text=${text}&maxLength=${maxLength}`); 
    }
}
